import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import Tours from './pages/Tours';
import TourDetail from './pages/TourDetail';
import Devices from './pages/Devices';
import MediaFiles from './pages/MediaFiles';
import VirtualWorlds from './pages/VirtualWorlds';
import GroupStats from './pages/GroupStats';
import TourStats from './pages/TourStats';
import Login from './pages/Login';
import CivitaControl from './pages/CivitaControl';
import AiAssistantSettingsPage from './pages/AiAssistantSettings';
import AppInstallations from './pages/AppInstallations';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
// Historia Ancient/Archaeological Color Palette
const HISTORIA_COLORS = {
  ancientGold: '#D4AF37',
  ancientBronze: '#CD7F32',
  papyrusBeige: '#F5E6D3',
  desertSand: '#E8C547',
  egyptianBlue: '#1034A6',
  steampunkCopper: '#B87333',
  oxidizedBronze: '#8B4513',
  ancientStone: '#8B7765',
  darkBronze: '#5C4033',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: HISTORIA_COLORS.ancientGold,
      dark: HISTORIA_COLORS.ancientBronze,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: HISTORIA_COLORS.ancientBronze,
      dark: HISTORIA_COLORS.oxidizedBronze,
      contrastText: '#FFFFFF',
    },
    background: {
      default: HISTORIA_COLORS.papyrusBeige,
      paper: '#FFFDF8',
    },
    text: {
      primary: HISTORIA_COLORS.darkBronze,
      secondary: HISTORIA_COLORS.ancientStone,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Georgia", "Times New Roman", serif',
    h6: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${HISTORIA_COLORS.ancientBronze} 0%, ${HISTORIA_COLORS.darkBronze} 100%)`,
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(180deg, ${HISTORIA_COLORS.papyrusBeige} 0%, #EDE4D6 100%)`,
          borderRight: `2px solid ${HISTORIA_COLORS.ancientGold}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: `${HISTORIA_COLORS.ancientGold}20`,
          },
          '&.Mui-selected': {
            backgroundColor: `${HISTORIA_COLORS.ancientGold}30`,
            borderLeft: `4px solid ${HISTORIA_COLORS.ancientGold}`,
            '&:hover': {
              backgroundColor: `${HISTORIA_COLORS.ancientGold}40`,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          background: `linear-gradient(135deg, ${HISTORIA_COLORS.ancientGold} 0%, ${HISTORIA_COLORS.ancientBronze} 100%)`,
          boxShadow: '0 3px 10px rgba(212, 175, 55, 0.4)',
          '&:hover': {
            background: `linear-gradient(135deg, ${HISTORIA_COLORS.desertSand} 0%, ${HISTORIA_COLORS.ancientGold} 100%)`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${HISTORIA_COLORS.ancientGold}40`,
          boxShadow: '0 4px 20px rgba(139, 75, 50, 0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/tours" replace />} />
              <Route path="tours" element={<Tours />} />
              <Route path="tours/:tourId" element={<TourDetail />} />
              <Route path="devices" element={<Devices />} />
              <Route path="app-installations" element={<AppInstallations />} />
              <Route path="media" element={<MediaFiles />} />
              <Route path="virtual-worlds" element={<VirtualWorlds />} />
              <Route path="group-stats" element={<GroupStats />} />
              <Route path="tour-stats" element={<TourStats />} />
              <Route path="ai-assistant" element={<AiAssistantSettingsPage />} />
              <Route path="civita-control" element={<CivitaControl />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
